// src/flipStorageBackend.js
import { lf } from './persist/migrateBothWays';

const KEY = 'persist:root';

// ---------- low-level helpers ----------
async function readIDB() { await lf.ready(); return lf.getItem(KEY); }
async function writeIDB(raw) { await lf.ready(); return lf.setItem(KEY, raw); }
async function removeIDB() { await lf.ready(); return lf.removeItem(KEY); }

function readLS() { try { return window.localStorage.getItem(KEY); } catch { return null; } }
function writeLS(raw) { window.localStorage.setItem(KEY, raw); }
function removeLS() { window.localStorage.removeItem(KEY); }

function byteLen(str) {
  if (str == null) return 0;
  try { return new Blob([String(str)]).size; } catch { return String(str).length; }
}
function truncate(str, n = 240) {
  if (str == null) return String(str);
  const s = String(str);
  return s.length > n ? s.slice(0, n) + '…(truncated)' : s;
}

// ---------- diagnostics ----------
export async function dumpPersist(tag = 'SNAPSHOT') {
  const lsBlob = readLS();
  const idbBlob = await readIDB();
  const backend = getSelectedBackend();
  console.log(`[persist ${tag}] backend(next boot):`, backend);
  console.log(`[persist ${tag}] LS  ${KEY} bytes=`, byteLen(lsBlob), ' preview=', truncate(lsBlob));
  console.log(`[persist ${tag}] IDB ${KEY} bytes=`, byteLen(idbBlob), ' preview=', truncate(idbBlob));
  return { lsBlob, idbBlob, backend };
}

export async function verifyPersistBackends() {
  const { lsBlob, idbBlob, backend } = await dumpPersist('VERIFY');
  const equal = String(lsBlob ?? '') === String(idbBlob ?? '');
  const result = {
    backendNextBoot: backend,
    lsBytes: byteLen(lsBlob),
    idbBytes: byteLen(idbBlob),
    equal,
  };
  console.log('[persist VERIFY] result =', result);
  return result;
}

// Which backend will store.js choose on next boot?
export function getSelectedBackend() {
  // FINAL PRECEDENCE: PREF → runtime.USE_LS → CRA → default(IDB)
  const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  const PREF = (typeof window !== 'undefined') ? window.localStorage.getItem('__persist:prefer_ls') : undefined;
  const CRA = (typeof process !== 'undefined' && process.env) ? process.env.REACT_APP_USE_LS : undefined;

  const toBool = (v) => {
    if (v === true || v === 'true' || v === '1' || v === 1) return true;
    if (v === false || v === 'false' || v === '0' || v === 0) return false;
    return undefined;
  };

  const USE_LS = (toBool(PREF) ?? toBool(runtime.USE_LS) ?? toBool(CRA) ?? false);
  return USE_LS ? 'localStorage' : 'indexedDB';
}

// ---------- on-demand flip ----------
/**
 * Flip redux-persist backend at runtime.
 * @param {'localStorage'|'indexedDB'} target
 * @param {{ keepOldCopy?: boolean }} [options]
 */
export async function flipStorageBackend(target, options) {
  console.log('flipStorageBackend target =', target, 'options =', options);
  if (typeof options !== 'object' || options == null) options = {};
  const { keepOldCopy = false } = options;

  if (typeof window === 'undefined') {
    console.warn('[flip] window undefined (SSR?) — abort');
    return;
  }
  if (target !== 'localStorage' && target !== 'indexedDB') {
    throw new Error("target must be 'localStorage' or 'indexedDB'");
  }
  const source = target === 'localStorage' ? 'indexedDB' : 'localStorage';

  await dumpPersist('BEFORE');

  // 1) read from source
  let blob = null;
  if (source === 'localStorage') blob = readLS();
  else blob = await readIDB();
  console.log('[flip] read from', source, '->', blob ? 'FOUND' : 'MISSING');

  // 2) write to target
  if (blob != null) {
    if (target === 'localStorage') writeLS(blob);
    else await writeIDB(blob);
    console.log('[flip] wrote to', target);
  } else {
    console.warn('[flip] nothing to write to target (source empty)');
  }

  // 3) set preference flag for next boot (PREF HAS PRIORITY)
  window.localStorage.setItem('__persist:prefer_ls', target === 'localStorage' ? '1' : '0');
  console.log('[flip] set __persist:prefer_ls =', window.localStorage.getItem('__persist:prefer_ls'));

  // 4) optionally remove legacy copy now
  if (!keepOldCopy) {
    console.log('[flip] removing legacy copy from', source);
    try { source === 'localStorage' ? removeLS() : await removeIDB(); }
    catch (e) { console.warn('[flip] legacy remove failed:', e); }
  } else {
    console.log('[flip] keepOldCopy = true → leaving legacy copy in place');
  }

  await dumpPersist('AFTER');

  // 5) reload so store boots with new backend
  console.log('[flip] reloading to hydrate from', target);
  window.location.reload();
}

// ---------- handy DevTools hook ----------
if (typeof window !== 'undefined') {
  window.debugPersist = async () => {
    const out = await verifyPersistBackends();
    return out;
  };
}


